import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const GalleryPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gallery`);
      if (response.ok) {
        const data = await response.json();
        setGalleryImages(data.images || []);
      }
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(galleryImages.map(img => img.category))];

  const filteredImages = selectedCategory === 'all' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === selectedCategory);

  const openLightbox = (image) => {
    setLightboxImage(image);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-slate-50 to-white pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ocean-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-slate-50 to-white pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-ocean-900 via-ocean-800 to-ocean-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Adventure Gallery</h1>
            <p className="text-xl opacity-90 max-w-3xl mx-auto mb-8">
              Explore stunning moments from our diving and camping adventures. 
              From underwater wonders to campfire connections - see what awaits you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white border-b border-slate-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-full font-semibold transition-all ${
                  selectedCategory === category
                    ? 'bg-coral-500 text-white shadow-lg'
                    : 'bg-slate-100 text-slate-700 hover:bg-coral-100 hover:text-coral-700'
                }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
          
          <div className="text-center mt-4">
            <span className="text-slate-600">
              Showing {filteredImages.length} {filteredImages.length === 1 ? 'image' : 'images'}
            </span>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredImages.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">📷</div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4">No images found</h3>
              <p className="text-slate-600 mb-8">Try selecting a different category</p>
              <button
                onClick={() => setSelectedCategory('all')}
                className="bg-coral-500 hover:bg-coral-600 text-white px-6 py-3 rounded-full font-semibold transition-all"
              >
                Show All Images
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredImages.map((image, index) => (
                <motion.div
                  key={image.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
                  onClick={() => openLightbox(image)}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={image.url || image.src}
                      alt={image.title}
                      className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    
                    {/* Overlay content */}
                    <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <h3 className="font-semibold text-sm mb-1">{image.title}</h3>
                      {image.description && (
                        <p className="text-xs text-white/90">{image.description}</p>
                      )}
                    </div>

                    {/* Category badge */}
                    <div className="absolute top-3 right-3">
                      <span className="bg-white/90 text-slate-700 px-2 py-1 rounded-full text-xs font-medium">
                        {image.category}
                      </span>
                    </div>

                    {/* View icon */}
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                        <span className="text-white text-xl">🔍</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <div className="relative max-w-6xl max-h-full">
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white text-3xl hover:text-coral-400 transition-colors z-10"
            >
              ×
            </button>
            
            <img
              src={lightboxImage.url || lightboxImage.src}
              alt={lightboxImage.title}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Image info */}
            <div className="absolute bottom-4 left-4 right-4 text-white bg-black/50 backdrop-blur-sm p-4 rounded-lg">
              <h3 className="text-lg font-semibold mb-1">{lightboxImage.title}</h3>
              {lightboxImage.description && (
                <p className="text-white/90">{lightboxImage.description}</p>
              )}
              <span className="inline-block mt-2 bg-coral-500 text-white px-3 py-1 rounded-full text-sm">
                {lightboxImage.category}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Instagram Feed Preview */}
      <section className="py-20 bg-gradient-to-r from-coral-500 to-coral-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Follow for Live Updates</h2>
          <p className="text-xl text-coral-100 mb-8 max-w-3xl mx-auto">
            See real-time adventures, behind-the-scenes moments, and get inspired by our community of ocean lovers
          </p>
          <div className="flex justify-center space-x-6">
            <a
              href="https://instagram.com/divecampadventures"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-coral-600 hover:bg-coral-50 px-8 py-4 rounded-full font-semibold transition-all transform hover:scale-105 flex items-center"
            >
              <span className="text-2xl mr-2">📷</span>
              Follow on Instagram
            </a>
            <a
              href="https://youtube.com/@divecampadventures"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border-2 border-white/30 hover:border-white/50 px-8 py-4 rounded-full font-semibold transition-all transform hover:scale-105 flex items-center"
            >
              <span className="text-2xl mr-2">📺</span>
              Watch on YouTube
            </a>
          </div>
        </div>
      </section>

      {/* Submit Photo Section */}
      <section className="py-20 bg-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-800 mb-4">Share Your Adventure</h2>
          <p className="text-xl text-slate-600 mb-8">
            Been on an adventure with us? We'd love to feature your photos in our gallery!
          </p>
          <div className="bg-white p-8 rounded-3xl shadow-lg">
            <div className="text-6xl mb-4">📸</div>
            <h3 className="text-xl font-bold text-slate-800 mb-4">Submit Your Photos</h3>
            <p className="text-slate-600 mb-6">
              Email your best adventure photos to us or tag us on social media. 
              We'll feature the best shots with your permission!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:ahmed@divecampadventures.com?subject=Photo Submission"
                className="bg-coral-500 hover:bg-coral-600 text-white px-6 py-3 rounded-full font-semibold transition-all"
              >
                📧 Email Photos
              </a>
              <a
                href="https://instagram.com/divecampadventures"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-slate-200 hover:bg-slate-300 text-slate-800 px-6 py-3 rounded-full font-semibold transition-all"
              >
                📷 Tag Us on Instagram
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GalleryPage;